import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Portal } from './Portal';
import {
  Clock,
  Table as TableIcon,
  User,
  CircleDot,
  ShoppingBag,
  Star,
  Printer,
} from 'lucide-react';
import { CartItem, Order, Category } from '../types';
import { getTicketSettings } from '../services/ticketSettings';
import type { TicketSettings } from '../services/ticketSettings';
import { getStoreInfo } from '../services/api';
// v5: système de design personnalisable
import {
  getConfirmationDesign,
  DEFAULT_DESIGN,
  RADIUS_CLASSES,
  BADGE_SHAPE_CLASSES,
  getNumberSizeClass,
  FONT_FAMILY_CLASSES,
  ENTER_ANIMATION_CLASSES,
  type ConfirmationDesignSettings,
} from '../services/confirmationDesignSettings';
import { ConfirmationFx } from './ConfirmationFx';
import { LottieCheckout } from './LottieCheckout';
import {
  calculateTicketTax,
  getTicketTaxSettings,
  type TicketTaxResult,
} from '../services/ticketTaxService';
import {
  TICKET_WIDTH,
  doubleSeparator,
  separatorLine,
  centerText,
  formatLine,
  formatCategoryHeader,
  formatQuantityItem,
  formatCustomization,
  formatTotalLine,
  aggregateCustomizationOptions,
  formatItemNote,
} from '../utils/ticketFormatter';
import { groupItemsByCategory } from '../utils/categoryGrouper';
import { settingsStorage, ProfileSettings } from '../services/settingsStorage';
import { getActiveProfile, getProfileById } from '../services/printerProfileService';
import { buildLegacyTicket } from '../utils/ticketFormatterLegacy';
import { buildModernTicket } from '../utils/ticketFormatterModern';
import { buildCustomTicket } from '../utils/ticketFormatterCustom';
import { buildMinimalTicket } from '../utils/ticketFormatterMinimal';
import { getMultiPrintConfig } from '../services/multiPrintService';
import { addPrintJob, getQueueStats } from '../services/printQueueService';
import { getRoutingConfig } from '../services/printerRoutingService';
import { buildRoutingPayload, type BuildPayloadOptions } from '../services/routingPayloadBuilder';
import { addRoutedPrintJob, startProcessing as startRoutedProcessing } from '../services/routedPrintQueueService';
import { formatOrderNumber } from '../utils/orderNumberFormatter';
import { DEMO_MODE } from '../services/demoMode';
// Modules d'impression portés depuis la console (unifié, Epson/Star, image...).
// Strictement additifs : sans profil porté ACTIF, rien de ce qui suit ne s'exécute.
import {
  isPortedProfileType,
  preparePortedTicket,
  buildPortedTicket,
} from '../utils/ticketModules';

const stripMarkers = (content: string): string => {
  return content
    .replace(/\[HEADER:START\]/g, '')
    .replace(/\[HEADER:END\]/g, '')
    .replace(/\[ITEMS:START\]/g, '')
    .replace(/\[ITEMS:END\]/g, '')
    .replace(/\[TOTALS:START\]/g, '')
    .replace(/\[TOTALS:END\]/g, '')
    .replace(/\[FOOTER:START\]/g, '')
    .replace(/\[FOOTER:END\]/g, '')
    .replace(/\[CAT:[^|]+\|[^:]+:START\]/g, '')
    .replace(/\[CAT:[^|]+\|[^:]+:END\]/g, '')
    .replace(/\[CAT:[^\]]+:START\]/g, '')
    .replace(/\[CAT:[^\]]+:END\]/g, '')
    .replace(/^\s*\n/gm, '');
};

const convertEscPosToHtml = (content: string): string => {
  let html = content;

  html = stripMarkers(html);

  const lines = html.split('\n');
  const result: string[] = [];
  let isBold = false;

  for (let line of lines) {
    if (line.includes('\x1B\x21\x18')) {
      line = line.replace(/\x1B\x21\x18/g, '');
      result.push(`<div style="font-size: 1.5em; font-weight: bold;">${line}</div>`);
    } else if (line.includes('\x1B\x21\x08')) {
      isBold = true;
      line = line.replace(/\x1B\x21\x08/g, '');
      if (line.trim()) {
        result.push(`<strong>${line}</strong>`);
      }
    } else if (line.includes('\x1B\x21\x00')) {
      isBold = false;
      line = line.replace(/\x1B\x21\x00/g, '');
      if (line.trim()) {
        result.push(line);
      }
    } else if (isBold && line.trim()) {
      result.push(`<strong>${line}</strong>`);
    } else {
      result.push(line);
    }
  }

  return result.join('\n');
};

interface OrderConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  orderType: 'takeaway' | 'dine-in';
  items: CartItem[];
  categories?: Category[];
  customerName?: string;
  tableNumber?: string;
  paymentMethod?: 'cash' | 'card';
  invoiceData?: any;
  orderId?: string;
  printSettings?: 'manual' | 'auto' | 'none';
}

export const OrderConfirmation: React.FC<OrderConfirmationProps> = ({
  isOpen,
  onClose,
  orderType,
  items,
  categories = [],
  customerName,
  tableNumber,
  paymentMethod,
  invoiceData,
  orderId,
  printSettings = 'manual',
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(10);
  const [settings, setSettings] = useState<TicketSettings | null>(null);
  const [profileSettings, setProfileSettings] = useState<ProfileSettings>({
    restaurantName: '',
    email: '',
    prepTime: '',
    terminalName: 'Borne 1',
    siretMessage: '',
    tvaMessage: '',
  });
  const [progress, setProgress] = useState(100);
  const [showContent, setShowContent] = useState(false);
  const [orderTotals, setOrderTotals] = useState({
    subtotal: 0,
    taxAmount: 0,
    invoiceTotal: 0,
  });
  const [orderCode, setOrderCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [taxResult, setTaxResult] = useState<TicketTaxResult | null>(null);
  // v5: paramètres de design de l'écran de confirmation
  const [design, setDesign] = useState<ConfirmationDesignSettings>(DEFAULT_DESIGN);
  const hasFetched = useRef(false);
  const hasPrintedRef = useRef(false);

  const storeInfo = getStoreInfo();
  const taxSettings = getTicketTaxSettings();

  // Enregistrer la commande dans localStorage
  const saveOrderToLocalStorage = (order: Order) => {
    const MAX_STORED_ORDERS = 50;
    const storedOrders = JSON.parse(localStorage.getItem('deviceOrders') || '[]');
    storedOrders.unshift(order);
    if (storedOrders.length > MAX_STORED_ORDERS) {
      storedOrders.pop();
    }
    localStorage.setItem('deviceOrders', JSON.stringify(storedOrders));
    console.log('Commande enregistrée dans localStorage:', order);
  };


  const buildProfessionalTicket = (): string => {
    const lines: string[] = [];
    const currency = 'EUR';
    const dateStr = new Date().toLocaleString('fr-FR');
    const orderTypeStr = orderType === 'takeaway' ? t('À emporter') : t('Sur place');
    const tableStr = tableNumber ? ` - ${t('Table')} ${tableNumber}` : '';
    const terminalName = profileSettings.terminalName || 'Borne 1';

    lines.push('[HEADER:START]');
    lines.push('\x1B\x21\x18');
    if (storeInfo?.name) {
      lines.push(centerText(storeInfo.name));
    }
    lines.push(doubleSeparator());
    lines.push(centerText(`**** #${formatOrderNumber(orderCode)} ****`));
    lines.push(centerText(terminalName));
    lines.push(doubleSeparator());
    lines.push('\x1B\x21\x00');
    lines.push(formatLine(`${t('Date')}:`, dateStr));
    lines.push(formatLine(`${t('Type')}:`, `${orderTypeStr}${tableStr}`));
    if (customerName) {
      lines.push(formatLine(`${t('Client')}:`, customerName));
    }
    lines.push('[HEADER:END]');

    lines.push(separatorLine('-'));
    lines.push('[ITEMS:START]');

    console.log('[buildProfessionalTicket] taxSettings.showCategories:', taxSettings.showCategories);
    console.log('[buildProfessionalTicket] categories.length:', categories.length);
    console.log('[buildProfessionalTicket] items.length:', items.length);

    // Toujours grouper par catégorie pour le routage, même si on ne l'affiche pas visuellement
    if (categories.length > 0) {
      const groupedItems = groupItemsByCategory(items, categories);
      const safeCategoryName = (name: string) => name.replace(/[|\]:]/g, '_');

      console.log('[buildProfessionalTicket] Grouped items:', groupedItems.length, 'groups');

      for (let i = 0; i < groupedItems.length; i++) {
        const group = groupedItems[i];
        const catName = safeCategoryName(group.categoryName);

        // TOUJOURS ajouter les marqueurs pour le routage (invisibles sur le ticket)
        lines.push(`[CAT:${group.categoryId}|${catName}:START]`);

        // Afficher l'en-tête de catégorie SEULEMENT si showCategories est activé
        if (taxSettings.showCategories) {
          lines.push(formatCategoryHeader(group.categoryName));
        }

        for (let itemIdx = 0; itemIdx < group.items.length; itemIdx++) {
          const item = group.items[itemIdx];
          const itemTotal = item.menuItem.price * item.quantity;
          lines.push(formatQuantityItem(item.quantity, item.menuItem.name, itemTotal, currency));

          for (const [, options] of Object.entries(item.customizations)) {
            const aggregated = aggregateCustomizationOptions(options);
            for (const opt of aggregated) {
              lines.push(formatCustomization(opt.name, opt.price > 0 ? opt.price : null, currency, TICKET_WIDTH, opt.quantity));
            }
          }

          if (item.note && item.note.trim() !== '') {
            const noteLines = formatItemNote(item.note, TICKET_WIDTH);
            lines.push(...noteLines);
          }

          if (itemIdx < group.items.length - 1) {
            lines.push('');
          }
        }

        // TOUJOURS fermer les marqueurs
        lines.push(`[CAT:${group.categoryId}|${catName}:END]`);

        if (i < groupedItems.length - 1 && taxSettings.showCategories) {
          lines.push(separatorLine('-'));
        }
      }
    } else {
      for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
        const item = items[itemIdx];
        const itemTotal = item.menuItem.price * item.quantity;
        lines.push(formatQuantityItem(item.quantity, item.menuItem.name, itemTotal, currency));

        for (const [, options] of Object.entries(item.customizations)) {
          const aggregated = aggregateCustomizationOptions(options);
          for (const opt of aggregated) {
            lines.push(formatCustomization(opt.name, opt.price > 0 ? opt.price : null, currency, TICKET_WIDTH, opt.quantity));
          }
        }

        if (item.note && item.note.trim() !== '') {
          const noteLines = formatItemNote(item.note, TICKET_WIDTH);
          lines.push(...noteLines);
        }

        if (itemIdx < items.length - 1) {
          lines.push('');
        }
      }
    }

    lines.push('[ITEMS:END]');
    lines.push(separatorLine('-'));

    lines.push('[TOTALS:START]');

    if (taxSettings.displayMode !== 'hidden') {
      if (taxSettings.showItemTaxDetail && taxResult && taxResult.itemDetails.length > 0) {
        lines.push(centerText(`-- ${t('Détail TVA par article')} --`));
        for (const item of taxResult.itemDetails) {
          const taxRateFormatted = item.taxRate % 1 === 0 ? item.taxRate.toFixed(0) : item.taxRate.toFixed(1);
          const taxStr = `TVA ${taxRateFormatted}%: ${item.taxAmount.toFixed(2)}${currency}`;
          lines.push(formatLine(`  ${item.name} x${item.quantity}`, taxStr));
        }
        lines.push(separatorLine('-'));
      }

      if (taxSettings.displayMode === 'detailed') {
        const subtotalHT = (taxResult?.subtotalHT ?? orderTotals.subtotal).toFixed(2);
        lines.push(formatLine(`${t('Sous-total HT')}:`, `${subtotalHT}${currency}`));

        const taxAmt = (taxResult?.taxAmount ?? orderTotals.taxAmount).toFixed(2);
        if (taxResult?.taxRate !== null) {
          const taxRateFormatted = (taxResult?.taxRate ?? 0) % 1 === 0 ? (taxResult?.taxRate ?? 0).toFixed(0) : (taxResult?.taxRate ?? 0).toFixed(1);
          lines.push(formatLine(`TVA (${taxRateFormatted}%):`, `${taxAmt}${currency}`));
        } else {
          lines.push(formatLine(`TVA:`, `${taxAmt}${currency}`));
        }

        // v5 fidélité : ligne séparée pour la réduction (ne contamine pas la TVA)
        const loyaltyDiscount = invoiceData?.loyalty_discount || 0;
        const loyaltyPointsUsed = invoiceData?.loyalty_points_used || 0;
        const hhDiscount = invoiceData?.happy_hour_discount || 0;
        const hhPercent = invoiceData?.happy_hour_percent || 0;
        if (hhDiscount > 0) {
          lines.push(formatLine(
            `${t('Happy Hour')} -${hhPercent}%:`,
            `-${hhDiscount.toFixed(2)}${currency}`
          ));
        }
        if (loyaltyDiscount > 0) {
          lines.push(formatLine(
            `${t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
            `-${loyaltyDiscount.toFixed(2)}${currency}`
          ));
        }

        lines.push(doubleSeparator());
        // Total final = total TTC - réduction
        const totalTTCBeforeDiscount = (taxResult?.totalTTC ?? orderTotals.invoiceTotal);
        const totalFinal = Math.max(0, totalTTCBeforeDiscount - loyaltyDiscount - hhDiscount);
        lines.push(formatTotalLine(`Total TTC:`, `${totalFinal.toFixed(2)}${currency}`, TICKET_WIDTH, true));
      } else if (taxSettings.displayMode === 'simple') {
        const taxAmt = (taxResult?.taxAmount ?? orderTotals.taxAmount).toFixed(2);
        if (taxResult?.taxRate !== null) {
          const taxRateFormatted = (taxResult?.taxRate ?? 0) % 1 === 0 ? (taxResult?.taxRate ?? 0).toFixed(0) : (taxResult?.taxRate ?? 0).toFixed(1);
          lines.push(formatLine(`TVA (${taxRateFormatted}%):`, `${taxAmt}${currency}`));
        } else {
          lines.push(formatLine(`TVA:`, `${taxAmt}${currency}`));
        }

        // v5 fidélité : ligne séparée pour la réduction
        const loyaltyDiscount = invoiceData?.loyalty_discount || 0;
        const loyaltyPointsUsed = invoiceData?.loyalty_points_used || 0;
        const hhDiscount = invoiceData?.happy_hour_discount || 0;
        const hhPercent = invoiceData?.happy_hour_percent || 0;
        if (hhDiscount > 0) {
          lines.push(formatLine(
            `${t('Happy Hour')} -${hhPercent}%:`,
            `-${hhDiscount.toFixed(2)}${currency}`
          ));
        }
        if (loyaltyDiscount > 0) {
          lines.push(formatLine(
            `${t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
            `-${loyaltyDiscount.toFixed(2)}${currency}`
          ));
        }

        lines.push(doubleSeparator());
        const totalBeforeDiscount = (taxResult?.totalTTC ?? orderTotals.invoiceTotal);
        const totalFinal = Math.max(0, totalBeforeDiscount - loyaltyDiscount - hhDiscount);
        lines.push(formatTotalLine(`${t('Total')}:`, `${totalFinal.toFixed(2)}${currency}`, TICKET_WIDTH, true));
      }
    } else {
      // v5 fidélité : ligne séparée pour la réduction (mode minimal)
      const loyaltyDiscount = invoiceData?.loyalty_discount || 0;
      const loyaltyPointsUsed = invoiceData?.loyalty_points_used || 0;
      const hhDiscount = invoiceData?.happy_hour_discount || 0;
      const hhPercent = invoiceData?.happy_hour_percent || 0;
      if (hhDiscount > 0) {
        lines.push(formatLine(
          `${t('Happy Hour')} -${hhPercent}%:`,
          `-${hhDiscount.toFixed(2)}${currency}`
        ));
      }
      if (loyaltyDiscount > 0) {
        lines.push(formatLine(
          `${t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
          `-${loyaltyDiscount.toFixed(2)}${currency}`
        ));
      }

      lines.push(doubleSeparator());
      const totalBeforeDiscount = (taxResult?.totalTTC ?? orderTotals.invoiceTotal);
      const totalFinal = Math.max(0, totalBeforeDiscount - loyaltyDiscount - hhDiscount);
      lines.push(formatTotalLine(`${t('Total')}:`, `${totalFinal.toFixed(2)}${currency}`, TICKET_WIDTH, true));
    }

    lines.push('\x1B\x21\x00');
    lines.push(doubleSeparator());
    const paymentStr = paymentMethod
      ? t(paymentMethod)
      : t('Non spécifié');
    lines.push(formatLine(`${t('Paiement')}:`, paymentStr));
    lines.push('[TOTALS:END]');

    if (profileSettings.siretMessage && profileSettings.siretMessage.trim() !== '') {
      lines.push(separatorLine('-'));
      lines.push(centerText(profileSettings.siretMessage));
    }
    if (profileSettings.tvaMessage && profileSettings.tvaMessage.trim() !== '') {
      lines.push(separatorLine('-'));
      lines.push(centerText(profileSettings.tvaMessage));
    }

    lines.push(separatorLine('-'));
    lines.push('[FOOTER:START]');
    lines.push(centerText(t('Merci de votre visite')));
    lines.push(doubleSeparator());
    lines.push('[FOOTER:END]');

    return lines.join('\n');
  };

  // v5 multi-imprimantes : construit l'objet ticketData (commun à tous les profils).
  const buildTicketDataForRender = () => {
    const loyaltyDiscount = invoiceData?.loyalty_discount || 0;
    const loyaltyPointsUsed = invoiceData?.loyalty_points_used || 0;
    const happyHourDiscount = invoiceData?.happy_hour_discount || 0;
    const happyHourName = invoiceData?.happy_hour_name || '';
    const happyHourPercent = invoiceData?.happy_hour_percent || 0;
    return {
      storeInfo, orderCode, items, categories, orderType, tableNumber, customerName,
      taxResult, orderTotals, paymentMethod, profileSettings, taxSettings, t,
      loyaltyDiscount, loyaltyPointsUsed, happyHourDiscount, happyHourName, happyHourPercent,
    };
  };

  /**
   * v5 multi-imprimantes : rend un ticket pour un profil donné, de façon SYNCHRONE.
   * ⚠️ Pour les profils PORTÉS (raster/unified/…), `preparePortedTicket()` doit avoir été
   * appelé AVANT (une seule fois) — fait dans handleRoutedPrinting. Sans ça, le module
   * raster se replie proprement sur le texte (repli sûr).
   */
  const renderTicketForProfileSync = (profile: any): string => {
    if (!profile) return buildProfessionalTicket();
    const ticketData = buildTicketDataForRender();
    if (isPortedProfileType(profile.type)) {
      return buildPortedTicket(profile.type, ticketData, profile);
    }
    switch (profile.type) {
      case 'legacy':
        return buildLegacyTicket(ticketData);
      case 'modern':
        return buildModernTicket(ticketData, profile);
      case 'custom':
        return buildCustomTicket(profile, ticketData);
      case 'minimal':
        return buildMinimalTicket(ticketData);
      default:
        return buildProfessionalTicket();
    }
  };

  const buildTicketForProfile = async (profile: any): Promise<string> => {
    // Les modules portés ont besoin du snapshot de routage pris AVANT la composition.
    if (isPortedProfileType(profile.type)) {
      await preparePortedTicket();
    }
    return renderTicketForProfileSync(profile);
  };

  const handleRoutedPrinting = async (effectiveOrderId: string, mode: string) => {
    try {
      console.log('[OrderConfirmation] Démarrage impression routée, mode:', mode);

      const activeProfile = await getActiveProfile();
      let fullTicketContent: string;

      if (!activeProfile) {
        console.warn('[OrderConfirmation] Aucun profil actif, utilisation du module par défaut');
        fullTicketContent = buildProfessionalTicket();
      } else {
        fullTicketContent = await buildTicketForProfile(activeProfile);
      }

      // v5 multi-imprimantes : prendre le snapshot de routage UNE fois (pour les profils
      // portés/raster), puis fournir un rendu SYNCHRONE par profil au builder de payload.
      await preparePortedTicket();

      const payloadOptions: BuildPayloadOptions = {
        fullTicketContent,
        orderNumber: orderCode || effectiveOrderId,
        orderDate: new Date().toLocaleString('fr-FR'),
        orderType,
        tableNumber,
        customerName,
        paymentMethod,
        // chaque imprimante (re)construit son contenu avec SON profil
        renderForProfile: (profile) => renderTicketForProfileSync(profile),
      };

      const payload = await buildRoutingPayload(payloadOptions);

      console.log('[OrderConfirmation] Payload routé généré:', {
        mode: payload.mode,
        ticketCount: payload.tickets.length,
        tickets: payload.tickets.map(t => ({
          printer: t.printerName,
          category: t.categoryName,
          isPrimary: t.isPrimary,
        })),
      });

      const jobId = await addRoutedPrintJob(effectiveOrderId, payload);
      console.log(`[OrderConfirmation] Job routé ajouté: ${jobId} - Système de retry automatique activé`);

      startRoutedProcessing();
    } catch (error) {
      console.error('[OrderConfirmation] Erreur impression routée:', error);
      setError('Erreur lors de l\'impression routée');
      hasPrintedRef.current = false;
      throw error;
    }
  };

  const printTicket = async () => {
    // v5 mode démo : AUCUNE impression (pas d'imprimante, prospect qui teste).
    if (DEMO_MODE) {
      console.log('[DEMO] Impression désactivée en mode démo.');
      return;
    }

    if (hasPrintedRef.current) {
      console.log("Impression déjà programmée pour cette commande.");
      return;
    }

    hasPrintedRef.current = true;

    try {
      const routingConfig = await getRoutingConfig();
      const effectiveOrderId = orderId || invoiceData?.order_id || `order-${Date.now()}`;

      if (routingConfig.enabled && routingConfig.mode !== 'classic') {
        console.log('[OrderConfirmation] Mode routage activé:', routingConfig.mode);
        await handleRoutedPrinting(effectiveOrderId, routingConfig.mode);
        return;
      }

      const multiPrintConfig = await getMultiPrintConfig();

      if (multiPrintConfig.enabled && multiPrintConfig.printJobs.length > 0) {
        console.log('[OrderConfirmation] Mode impression multiple activé', multiPrintConfig.printJobs);

        let jobCount = 0;

        for (const printJob of multiPrintConfig.printJobs) {
          const profile = await getProfileById(printJob.profileId);
          if (!profile) {
            console.warn(`[OrderConfirmation] Profil ${printJob.profileId} introuvable, ignoré`);
            continue;
          }

          const rawTicket = await buildTicketForProfile(profile);
          const ticketContent = stripMarkers(rawTicket);

          for (let copy = 1; copy <= printJob.copies; copy++) {
            try {
              await addPrintJob(
                effectiveOrderId,
                ticketContent,
                profile.id,
                profile.name,
                copy,
                printJob.copies,
                5
              );
              jobCount++;
              console.log(`[OrderConfirmation] Job d'impression ajouté: ${profile.name} copie ${copy}/${printJob.copies}`);
            } catch (error) {
              console.error(`[OrderConfirmation] Erreur ajout job ${profile.name} copie ${copy}:`, error);
            }
          }
        }

        if (jobCount > 0) {
          console.log(`[OrderConfirmation] ✓ ${jobCount} job(s) d'impression ajouté(s) à la file avec système de retry automatique`);

          const stats = await getQueueStats();
          console.log('[OrderConfirmation] Statistiques file d\'impression:', stats);
        } else {
          setError("Aucun travail d'impression n'a pu être ajouté.");
        }
      } else {
        const activeProfile = await getActiveProfile();

        let rawTicket: string;

        if (!activeProfile) {
          console.warn('[OrderConfirmation] Aucun profil actif, utilisation du module par défaut');
          rawTicket = buildProfessionalTicket();
        } else {
          rawTicket = await buildTicketForProfile(activeProfile);
        }

        const ticketContent = stripMarkers(rawTicket);

        try {
          const jobId = await addPrintJob(
            effectiveOrderId,
            ticketContent,
            activeProfile?.id,
            activeProfile?.name || 'Défaut',
            1,
            1,
            5
          );
          console.log(`[OrderConfirmation] ✓ Job d'impression ajouté avec ID: ${jobId} - Système de retry automatique activé`);

          const stats = await getQueueStats();
          console.log('[OrderConfirmation] Statistiques file d\'impression:', stats);
        } catch (error) {
          console.error('[OrderConfirmation] Erreur ajout job d\'impression:', error);
          setError("Impossible d'ajouter le travail d'impression à la file.");
        }
      }
    } catch (error) {
      console.error('[OrderConfirmation] Erreur lors de la préparation de l\'impression:', error);
      setError("Erreur lors de la génération du ticket.");
      hasPrintedRef.current = false;
    }
  };

  useEffect(() => {
    console.log('OrderConfirmation monté avec :', {
      isOpen,
      orderId,
      hasInvoiceData: !!invoiceData,
    });
    return () => console.log('OrderConfirmation démonté');
  }, []);

  // v5: Chargement des paramètres de design + écoute des updates admin
  useEffect(() => {
    let mounted = true;
    const loadDesign = async () => {
      try {
        const d = await getConfirmationDesign();
        if (mounted) setDesign(d);
      } catch (e) {
        console.error('[v5] Erreur chargement design confirmation:', e);
      }
    };
    loadDesign();

    const handleUpdate = (e: Event) => {
      const ce = e as CustomEvent<ConfirmationDesignSettings>;
      if (ce.detail && typeof ce.detail === 'object') {
        setDesign(ce.detail);
      }
    };
    window.addEventListener('confirmationDesignUpdated', handleUpdate);
    return () => {
      mounted = false;
      window.removeEventListener('confirmationDesignUpdated', handleUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchOrderData = async () => {
      const effectiveOrderId = orderId || invoiceData?.order_id;
      if (!effectiveOrderId || !invoiceData) {
        console.log('orderId ou invoiceData manquant', {
          effectiveOrderId,
          hasInvoiceData: !!invoiceData,
        });
        return;
      }

      const payload = {
        store_id: invoiceData.store?._id,
        server_token: invoiceData.store?.server_token,
        order_id: effectiveOrderId,
      };

      console.log('Lancement du fetch avec payload :', JSON.stringify(payload, null, 2));

      try {
        const response = await fetch('https://api.restomi.fr/v3/api/store/get_order_data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Erreur HTTP : ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        console.log('Réponse complète de l’API :', JSON.stringify(data, null, 2));

        const uniqueId = data.order?.unique_id || data.order_payment?.unique_id || 'N/A';
        setOrderCode(uniqueId.toString());
      } catch (error) {
        console.error('Erreur lors du fetch :', error);
        setOrderCode('Erreur');
      }
    };

    const effectiveOrderId = orderId || invoiceData?.order_id;
    if (isOpen && effectiveOrderId && !hasFetched.current) {
      console.log('Conditions remplies, lancement de fetchOrderData');
      hasFetched.current = true;
      fetchOrderData();
    }
  }, [isOpen, orderId, invoiceData]);

  useEffect(() => {
    if (
      orderCode &&
      orderCode !== 'N/A' &&
      orderCode !== 'Erreur' &&
      orderTotals.invoiceTotal > 0
    ) {
      const terminalName = profileSettings.terminalName || 'Borne 1';

      const order: Order = {
        id: orderId || orderCode,
        items: items.map((item) => ({
          menuItem: {
            id: item.menuItem.id,
            name: item.menuItem.name,
            price: item.menuItem.price,
          },
          quantity: item.quantity,
          customizations: item.customizations,
        })),
        total: orderTotals.invoiceTotal,
        status: 'pending',
        createdAt: new Date().toISOString(),
        customerId: customerName || 'anonymous',
        orderType,
        tableNumber,
        paymentMethod,
        terminalName,
      };

      saveOrderToLocalStorage(order);
    }
  }, [
    orderCode,
    orderTotals,
    items,
    orderId,
    customerName,
    orderType,
    tableNumber,
    paymentMethod,
    profileSettings.terminalName,
  ]);

  useEffect(() => {
    if (invoiceData) {
      const subtotal = invoiceData.subtotal || 0;
      // v5 fidélité: si réduction appliquée, displayTotal est déjà réduit.
      // Pour ne PAS contaminer le calcul de la TVA, on récupère le total AVANT
      // réduction. La réduction sera affichée comme ligne séparée.
      const loyaltyDiscount = invoiceData.loyalty_discount || 0;
      const happyHourDiscount = invoiceData.happy_hour_discount || 0;
      const displayTotal = invoiceData.displayTotal || 0; // total final (réduit)
      // Total AVANT toute remise → la TVA ne doit jamais être contaminée
      const invoiceTotal = displayTotal + loyaltyDiscount + happyHourDiscount;
      const taxAmount = invoiceTotal - subtotal;
      console.log('Mise à jour de orderTotals avec invoiceData :', invoiceData, 'Résultat :', {
        subtotal,
        taxAmount,
        invoiceTotal,
        displayTotal,
        loyaltyDiscount,
      });
      setOrderTotals({ subtotal, taxAmount, invoiceTotal });

      const itemsForTax = items.map((item) => ({
        menuItem: {
          name: item.menuItem.name,
          price: item.menuItem.price,
          tax_details: (item.menuItem as any).tax_details,
        },
        quantity: item.quantity,
      }));
      const result = calculateTicketTax(itemsForTax, { subtotal, displayTotal: invoiceTotal });
      setTaxResult(result);

      if (
        printSettings === 'auto' &&
        orderCode &&
        orderCode !== 'N/A' &&
        orderCode !== 'Erreur'
      ) {
        console.log('Impression automatique déclenchée après mise à jour de orderTotals');
        printTicket();
      }
    }
  }, [invoiceData, orderCode, printSettings, items]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setShowContent(true), 100);
    } else {
      setShowContent(false);
      hasFetched.current = false;
      hasPrintedRef.current = false;
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setShowContent(false);
    setTimeout(() => {
      setCountdown(0);
      onClose();
    }, 500);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setCountdown(10);
      setProgress(100);
      document.body.style.overflow = 'hidden';

      const loadSettings = async () => {
        try {
          const ticketSettings = await getTicketSettings();
          setSettings(ticketSettings);

          const profile = await settingsStorage.getProfileSettings();
          setProfileSettings(profile);
          console.log('[OrderConfirmation] Profile chargé depuis IndexedDB:', {
            siretMessage: profile.siretMessage,
            tvaMessage: profile.tvaMessage,
            terminalName: profile.terminalName,
            restaurantName: profile.restaurantName,
          });
        } catch (error) {
          console.error('Erreur lors du chargement des paramètres:', error);
        }
      };

      loadSettings();
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (isOpen && countdown > 0) {
      timerId = setInterval(() => {
        setCountdown((prev) => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            handleClose();
          }
          return newValue;
        });
      }, 1000);
    }
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [isOpen, countdown, handleClose]);

  useEffect(() => {
    setProgress((countdown / 10) * 100);
  }, [countdown]);

  if (!isOpen || !settings) return null;

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  const { subtotal, taxAmount, invoiceTotal } = orderTotals;

  // v5: helpers pour appliquer le design dynamique
  const popupRadiusClass = RADIUS_CLASSES[design.popupRadius] || 'rounded-3xl';
  const badgeShapeClass = BADGE_SHAPE_CLASSES[design.badgeShape] || 'rounded-full';
  const numberSizeClass = getNumberSizeClass(design.numberSize);
  const fontFamilyClass = FONT_FAMILY_CLASSES[design.fontFamily] || '';
  const enterAnimClass = ENTER_ANIMATION_CLASSES[design.enterAnimation] || '';

  // v5: détecter si le fond est clair (luminance > 0.6) pour adapter les overlays
  const isLightBackground = (() => {
    const hex = design.bgColor1.replace('#', '');
    if (hex.length < 6) return false;
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 0.6;
  })();
  // Couleur d'overlay pour les badges (inversée si fond clair)
  const overlayBg = isLightBackground ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)';
  const overlayBorder = isLightBackground ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.2)';

  // Style du numéro selon numberStyle
  const numberStyleClass = (() => {
    switch (design.numberStyle) {
      case 'glow': return 'confirmation-number-glow';
      case 'neon': return 'confirmation-number-neon';
      case 'outline': return 'confirmation-number-outline';
      default: return '';
    }
  })();

  // Style du fond du header (gradient + image éventuelle)
  const headerBgStyle: React.CSSProperties = {
    background: `linear-gradient(135deg, ${design.bgColor1} 0%, ${design.bgColor2} 100%)`,
    color: design.textColor,
  };

  const isMinimalTemplate = design.template === 'minimal';

  return (
    <Portal>
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-md">
        <div
          className="absolute inset-0 animate-gradient"
          style={{
            background: `linear-gradient(135deg, ${design.bgColor1}1A 0%, ${design.bgColor2}1A 100%)`,
          }}
        />
      </div>

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div
          className={`w-full max-w-2xl transform transition-all duration-500 ${enterAnimClass} ${
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <div className={`relative bg-white/10 backdrop-blur-xl ${popupRadiusClass} shadow-2xl overflow-hidden border border-white/20`}>
            {error && (
              <div className="p-4 bg-red-100 text-red-700 rounded-t-lg">
                {error}
              </div>
            )}
            <div className="absolute top-0 left-0 w-full h-2 z-10">
              <div
                className="absolute inset-0 animate-pulse opacity-50"
                style={{ background: `linear-gradient(90deg, ${design.bgColor1}, ${design.bgColor2})` }}
              />
              <div
                className="absolute h-full"
                style={{
                  background: `linear-gradient(90deg, ${design.bgColor1}, ${design.bgColor2})`,
                  width: `${progress}%`,
                  transition: 'width 1s linear',
                }}
              />
            </div>

            {/* Header avec design v5 */}
            <div
              className={`relative pt-16 pb-20 px-8 overflow-hidden ${fontFamilyClass}`}
              style={headerBgStyle}
            >
              {/* Image de fond */}
              {design.backgroundImage && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: `url(${design.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: design.backgroundOpacity / 100,
                    filter: design.backgroundBlur > 0 ? `blur(${design.backgroundBlur}px)` : undefined,
                  }}
                />
              )}

              {/* Effet glow autour du popup */}
              {design.fxType === 'glow' && (
                <div className="absolute inset-0 pointer-events-none confirmation-fx-glow" />
              )}

              {/* Effets visuels (confettis, particules, sparkles) */}
              <ConfirmationFx type={design.fxType} color={design.numberColor} />

              <div className="space-y-4 relative z-10">
                {/* Message de félicitations custom (optionnel) */}
                {design.congratsMessage && (
                  <div className="text-center mb-2">
                    <p className="text-sm opacity-90 italic">
                      {design.congratsMessage}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div
                    className={`flex items-center gap-2 backdrop-blur-sm ${badgeShapeClass} px-4 py-2 border`}
                    style={{ backgroundColor: overlayBg, borderColor: overlayBorder }}
                  >
                    <Star className="w-5 h-5" style={{ color: design.numberColor }} />
                    <span className="text-lg font-medium">
                      {t('Commande confirmée')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CircleDot className="w-3 h-3 animate-pulse" style={{ color: design.numberColor }} />
                    <span className="opacity-90">
                      {formatDate(new Date())}
                    </span>
                  </div>
                </div>
                {orderCode && (
                  <div className="text-center">
                    <span className="text-lg font-medium">
                      {t('Numéro de commande')}
                    </span>
                    <div className="mt-2">
                      <span
                        className={`${numberSizeClass} font-bold ${numberStyleClass} ${
                          design.numberStyle === 'gradient' && !isMinimalTemplate
                            ? 'bg-clip-text text-transparent'
                            : ''
                        }`}
                        style={
                          design.numberStyle === 'gradient' && !isMinimalTemplate
                            ? {
                                backgroundImage: `linear-gradient(135deg, ${design.numberColor}, ${design.textColor})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                              }
                            : { color: design.numberColor }
                        }
                      >
                        {formatOrderNumber(orderCode)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* v5: Animation Lottie checkout (optionnelle, lazy-loaded) */}
            {design.showLottieAnimation && isOpen && (
              <div className="flex justify-center py-2 bg-gradient-to-b from-white/5 to-white/10">
                <LottieCheckout
                  active={design.showLottieAnimation}
                  loop={design.lottieLoop}
                  size={design.lottieSize}
                />
              </div>
            )}

            <div className="flex flex-col max-h-[calc(100vh-200px)] bg-gradient-to-b from-white/5 to-white/10">
              <div className="flex-1 overflow-y-auto p-8 space-y-8 hide-scrollbar">
                {design.showOrderType && (
                <div className="flex items-center justify-between p-4 bg-white/50 backdrop-blur-sm rounded-2xl border border-orange-100/20">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      {orderType === 'takeaway' ? (
                        <ShoppingBag className="w-6 h-6 text-orange-600" />
                      ) : (
                        <TableIcon className="w-6 h-6 text-orange-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {orderType === 'takeaway'
                          ? t('À emporter')
                          : t('Sur place')}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {orderType === 'takeaway'
                          ? t('Récupération au comptoir')
                          : t('Table', { number: tableNumber })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-orange-50 text-orange-600 px-4 py-2 rounded-xl">
                      <Clock className="w-5 h-5" />
                      <span className="font-medium">{t('Temps estimé')}</span>
                    </div>
                  </div>
                </div>
                )}

                {design.showCustomerName && customerName && (
                  <div className="flex items-center gap-4 p-4 bg-white/50 backdrop-blur-sm rounded-2xl border border-orange-100/20">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <User className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('Client')}</p>
                      <p className="font-medium text-gray-900">{customerName}</p>
                    </div>
                  </div>
                )}

                {design.showOrderDetails && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">
                    {t('Détails de la commande')}
                  </h3>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {items.map((item, index) => (
                      <div
                        key={`${item.menuItem.id}-${index}`}
                        className="flex justify-between items-start p-4 bg-white/50 backdrop-blur-sm rounded-2xl border border-orange-100/20"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-orange-600">
                              {item.quantity}×
                            </span>
                            <span className="font-medium text-gray-900">
                              {item.menuItem.name}
                            </span>
                          </div>
                          {Object.entries(item.customizations).map(
                            ([specId, options], optIndex) => {
                              const aggregated = aggregateCustomizationOptions(options);
                              return (
                                <div
                                  key={`${item.menuItem.id}-${specId}-${optIndex}`}
                                  className="text-sm text-gray-500 ml-6"
                                >
                                  {aggregated.map((opt, i) => (
                                    <span
                                      key={`${item.menuItem.id}-${specId}-${opt.id}-${i}`}
                                    >
                                      {opt.name}
                                      {opt.quantity > 1 && ` x${opt.quantity}`}
                                      {opt.price > 0 &&
                                        ` (${t('Prix ajout', {
                                          price: (opt.price * opt.quantity).toFixed(2),
                                        })})`}
                                      {i < aggregated.length - 1 ? ', ' : ''}
                                    </span>
                                  ))}
                                </div>
                              );
                            }
                          )}
                        </div>
                        <span className="font-semibold text-gray-900">
                          {t('Prix', {
                            price: (item.menuItem.price * item.quantity).toFixed(
                              2
                            ),
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {design.showPriceBreakdown && (
                <div className="space-y-3 p-4 bg-white/50 backdrop-blur-sm rounded-2xl border border-orange-100/20">
                  {taxSettings.showItemTaxDetail && taxResult && taxResult.itemDetails.length > 0 && (
                    <div className="pb-3 border-b border-orange-100/20">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {t('Détail TVA par article')}
                      </p>
                      <div className="space-y-1">
                        {taxResult.itemDetails.map((item, idx) => {
                          const taxRateFormatted = item.taxRate % 1 === 0 ? item.taxRate.toFixed(0) : item.taxRate.toFixed(1);
                          return (
                            <div key={idx} className="flex justify-between text-xs text-gray-500">
                              <span>{item.name} x{item.quantity}</span>
                              <span>
                                {t('TVA', { rate: taxRateFormatted })}: {t('Prix', { price: item.taxAmount.toFixed(2) })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {taxSettings.displayMode === 'detailed' && (
                    <>
                      <div className="flex justify-between text-gray-600">
                        <span>{t('Sous-total HT')}</span>
                        <span>{t('Prix', { price: (taxResult?.subtotalHT ?? subtotal).toFixed(2) })}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>
                          {taxResult?.taxRate !== null
                            ? (() => {
                                const taxRateFormatted = (taxResult?.taxRate ?? 0) % 1 === 0
                                  ? (taxResult?.taxRate ?? 0).toFixed(0)
                                  : (taxResult?.taxRate ?? 0).toFixed(1);
                                return t('TVA', { rate: taxRateFormatted });
                              })()
                            : t('TVA totale')}
                        </span>
                        <span>{t('Prix', { price: (taxResult?.taxAmount ?? taxAmount).toFixed(2) })}</span>
                      </div>
                    </>
                  )}

                  {taxSettings.displayMode === 'simple' && (
                    <div className="flex justify-between text-gray-600">
                      <span>
                        {taxResult?.taxRate !== null
                          ? (() => {
                              const taxRateFormatted = (taxResult?.taxRate ?? 0) % 1 === 0
                                ? (taxResult?.taxRate ?? 0).toFixed(0)
                                : (taxResult?.taxRate ?? 0).toFixed(1);
                              return t('TVA', { rate: taxRateFormatted });
                            })()
                          : t('TVA totale')}
                      </span>
                      <span>{t('Prix', { price: (taxResult?.taxAmount ?? taxAmount).toFixed(2) })}</span>
                    </div>
                  )}

                  {/* v5 happy hour: ligne remise si applicable (NF525) */}
                  {(invoiceData?.happy_hour_discount || 0) > 0 && (
                    <div className="flex justify-between text-orange-600 font-semibold">
                      <span className="flex items-center gap-1">
                        🎉 {t('Happy Hour')} −{invoiceData?.happy_hour_percent || 0}%
                      </span>
                      <span>−{t('Prix', { price: (invoiceData?.happy_hour_discount || 0).toFixed(2) })}</span>
                    </div>
                  )}

                  {/* v5 fidélité: ligne réduction si applicable */}
                  {(invoiceData?.loyalty_discount || 0) > 0 && (
                    <div className="flex justify-between text-orange-600 font-semibold">
                      <span className="flex items-center gap-1">
                        🎁 {t('Reduction fidelite')} ({invoiceData?.loyalty_points_used || 0} pts)
                      </span>
                      <span>−{t('Prix', { price: (invoiceData?.loyalty_discount || 0).toFixed(2) })}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-xl font-bold pt-3 border-t border-orange-100/20">
                    <span>{taxSettings.displayMode === 'detailed' ? t('Total TTC') : t('Total')}</span>
                    <span className="text-orange-600">
                      {t('Prix', {
                        price: Math.max(
                          0,
                          (taxResult?.totalTTC ?? invoiceTotal)
                            - (invoiceData?.loyalty_discount || 0)
                            - (invoiceData?.happy_hour_discount || 0)
                        ).toFixed(2)
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600 mt-2">
                    <span>{t('Méthode de paiement')}</span>
                    <span>
                      {paymentMethod
                        ? t(paymentMethod)
                        : t('Non spécifié')}
                    </span>
                  </div>
                </div>
                )}
              </div>

              <div className="flex-shrink-0 p-8 pt-4 space-y-6 bg-gradient-to-t from-white/10 to-transparent">
                {/* v5 mode démo : pas de bouton d'impression (aucune imprimante) */}
                {!DEMO_MODE && printSettings === 'manual' && (
                  <button
                    onClick={printTicket}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl transition-colors"
                    style={{ backgroundColor: 'var(--color-confirm-button, #10B981)', color: 'var(--color-confirm-button-text, #FFFFFF)' }}
                    aria-label={t('Imprimer le ticket.aria')}
                  >
                    <Printer size={20} />
                    <span>{t('Imprimer le ticket')}</span>
                  </button>
                )}
                <p className="text-gray-600 font-medium text-center">
                  {settings.footerMessage}
                </p>
                <button
                  onClick={handleClose}
                  className="w-full px-6 py-3 rounded-xl transition-colors font-semibold"
                  style={{ backgroundColor: 'var(--color-confirm-button, #10B981)', color: 'var(--color-confirm-button-text, #FFFFFF)' }}
                  aria-label={t('Fermer la confirmation.aria')}
                >
                  {t('Fermer', { countdown })}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
};